import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { OrganizationFeatureWire } from "@releve/shared";
import { useOrganization } from "@/contexts/OrganizationContext";
import { authedGet } from "@/lib/api";

/**
 * Shared query for the effective feature flags of the current organization,
 * read from `GET /api/v1/subscriptions/orgs/:orgId/features` — the backend's
 * `get_organization_features`, i.e. THE resolution rule (docs/SUBSCRIPTIONS.md,
 * docs/FEATURE_FLAGS.md): an `organization_features` override decides; else a
 * feature that is in any subscription plan is on only in the org's live plan;
 * else `feature_registry.default_enabled`. The rule lives in SQL only — never
 * re-derive it here.
 *
 * Fails closed: a failed request THROWS (React Query retries) rather than
 * caching a partial answer, so every feature reads as disabled meanwhile.
 *
 * Both useFeatureEnabled and useEnabledFeatures consume this to share
 * a single React Query cache entry (`['org-features', orgId]`).
 */
function useOrganizationFeaturesQuery() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useQuery<OrganizationFeatureWire[]>({
    queryKey: ["org-features", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const rows = await authedGet<OrganizationFeatureWire[]>(`/api/v1/subscriptions/orgs/${orgId}/features`);
      if (!Array.isArray(rows)) throw new Error("Unreadable feature flags response");
      return rows;
    },
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });
}

/**
 * Check if a specific feature is enabled for the current organization.
 *
 * Returns `{ enabled, isLoading }`. `enabled` is `false` if the feature is
 * not effectively enabled for the org (see the rule above) or the query is
 * still loading.
 *
 * @example
 * ```tsx
 * const { enabled } = useFeatureEnabled('voice_control');
 * if (enabled) {
 *   // render feature UI
 * }
 * ```
 */
export function useFeatureEnabled(featureKey: string): { enabled: boolean; isLoading: boolean } {
  const { selectedOrganization } = useOrganization();
  const { data, isLoading } = useOrganizationFeaturesQuery();

  const feature = data?.find((f) => f.feature_key === featureKey);
  // Treat "org not yet selected" as loading to prevent FeatureGate
  // from redirecting before the org context has synced with the URL.
  const orgNotReady = !selectedOrganization;
  return {
    enabled: feature?.enabled === true,
    isLoading: isLoading || orgNotReady,
  };
}

/**
 * Returns a Set of all enabled feature keys for the current organization.
 * Useful for filtering collections (e.g., navigation items) without
 * calling useFeatureEnabled in a loop.
 */
export function useEnabledFeatures(): Set<string> {
  const { data } = useOrganizationFeaturesQuery();

  return useMemo(
    () => new Set((data ?? []).filter((f) => f.enabled).map((f) => f.feature_key)),
    [data]
  );
}
